import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action } from '@ngrx/store';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap, reduce, switchMap, tap } from 'rxjs/operators';

import {
  CompanyApi,
  EquipmentApi,
  Gateway,
  Installation,
  UserRegistrationApi,
  UsersApi,
  ViError,
} from '@vi-iot/client';

import { hasMainDeviceRole } from '../installation-details';

import { InstallationsActions } from '../installations';
import { UserDetailsMessageService } from './user-details-message.service';
import { UserDetailsActions } from './user-details.actions';
import { userGatewaysInfiniteScrollLimit } from './user-details.model';
import { UserDetailsStore } from './user-details.store';

@Injectable()
export class UserDetailsEffects {
  getUserDetails$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserDetailsActions.getUserDetails),
      switchMap((action) =>
        this.userRegistrationApi.getUserDetails(action.userId).pipe(
          map((response) =>
            UserDetailsActions.getUserDetailsSuccess({
              userId: action.userId,
              response: {
                userDetails: response,
              },
            })
          ),
          catchError((err) => {
            if (err && err.statusCode === 404) {
              return of(UserDetailsActions.verifyIfUserIsOma.attempt({ userId: action.userId }));
            }

            return of(UserDetailsActions.getUserDetailsError({ error: err }));
          })
        )
      )
    );
  });

  verifyIfUserIsOma$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserDetailsActions.verifyIfUserIsOma.attempt),
      switchMap((action) =>
        this.usersApi.getUser(action.userId).pipe(
          map((data) =>
            UserDetailsActions.verifyIfUserIsOma.success({ userId: action.userId, user: data })
          ),
          catchError((error) => {
            this.router.navigate(['/not-found']);

            return of(UserDetailsActions.verifyIfUserIsOma.error({ error }));
          })
        )
      )
    );
  });

  onGetUserDetailsSuccess$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        UserDetailsActions.getUserDetailsSuccess,
        UserDetailsActions.verifyIfUserIsOma.success
      ),
      map((action) =>
        UserDetailsActions.getUserInstallations({
          userId: action.userId,
          limit: userGatewaysInfiniteScrollLimit,
          cursor: '',
        })
      )
    );
  });

  onGetUserDetailsSuccess2$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserDetailsActions.getUserDetailsSuccess),
      map((action) => UserDetailsActions.readUserDetails({ userId: action.userId }))
    );
  });

  readUserDetails$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserDetailsActions.readUserDetails),
      switchMap((action) =>
        this.usersApi.getUser(action.userId).pipe(
          map((user) =>
            UserDetailsActions.readUserDetailsSuccess({
              user,
            })
          ),
          catchError((error: ViError) => of(UserDetailsActions.readUserDetailsError({ error })))
        )
      )
    );
  });

  getUserInstallations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserDetailsActions.getUserInstallations),
      switchMap((action) => {
        // decode cursor so it doesn't become encoded twice
        const cursor = action.cursor ? decodeURIComponent(action.cursor) : undefined;

        return this.getEndUserInstallations(action.userId, action.limit, cursor, true);
      })
    );
  });

  getLockedInstallations$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserDetailsActions.getUserInstallations),
      map(() => InstallationsActions.listLockedInstallations())
    );
  });

  changeUserDetails$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserDetailsActions.changeUserDetails),
      concatLatestFrom(() => this.userDetailsStore.state$),
      map(([action, state]) => ({
        ...action,
        userId: state.userDetails.id,
      })),
      switchMap((action) =>
        this.userRegistrationApi.changeUserDetails(action.userId, action.body).pipe(
          tap(() => {
            if (action.body.userState) {
              this.messageService.info(this.messageService.messages.changeUser.success);
            }
          }),
          map((response) =>
            UserDetailsActions.changeUserDetailsSuccess({
              userId: action.userId,
              body: action.body,
              response,
            })
          ),
          catchError((error) => {
            this.messageService.error(this.messageService.messages.changeUser.error);

            return of(UserDetailsActions.changeUserDetailsError({ error }));
          })
        )
      )
    );
  });

  changeOmaUserDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserDetailsActions.changeOmaUserDetails.attempt),
      concatLatestFrom(() => [this.userDetailsStore.userId$]),
      switchMap(([action, userId]) =>
        this.usersApi.updateOmaUser(action.body, userId).pipe(
          map((user) => UserDetailsActions.changeOmaUserDetails.success({ user })),
          catchError((error) => of(UserDetailsActions.changeOmaUserDetails.error({ error })))
        )
      )
    );
  });

  resendConfirmationEmail$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserDetailsActions.resendConfirmationEmail),
      switchMap((action) =>
        this.userRegistrationApi.resendUserEmailConfirmation({ loginId: action.loginId }).pipe(
          tap(() =>
            this.messageService.info(this.messageService.messages.resendConfirmationEmail.success)
          ),
          map(() => UserDetailsActions.resendConfirmationEmailSuccess()),
          catchError((error) => {
            this.messageService.error(this.messageService.messages.resendConfirmationEmail.error);

            return of(UserDetailsActions.resendConfirmationEmailError({ error }));
          })
        )
      )
    );
  });

  requestPasswordReset$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserDetailsActions.requestPasswordReset),
      switchMap((action) =>
        this.userRegistrationApi.resetUserPassword({ loginId: action.loginId }).pipe(
          tap(() =>
            this.messageService.info(this.messageService.messages.requestPasswordReset.success)
          ),
          map(() => UserDetailsActions.requestPasswordResetSuccess()),
          catchError((error) => {
            this.messageService.error(this.messageService.messages.requestPasswordReset.error);

            return of(UserDetailsActions.requestPasswordResetError({ error }));
          })
        )
      )
    );
  });

  getUserCompany$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserDetailsActions.getUserCompany),
      switchMap((action) =>
        this.companyApi.getUserCompany(action.customerNumber).pipe(
          map((response) => UserDetailsActions.getUserCompanySuccess({ company: response })),
          catchError((error) => of(UserDetailsActions.getUserCompanyError({ error })))
        )
      )
    );
  });

  constructor(
    private router: Router,
    private actions$: Actions,
    private userRegistrationApi: UserRegistrationApi,
    private equipmentApi: EquipmentApi,
    private usersApi: UsersApi,
    private messageService: UserDetailsMessageService,
    private companyApi: CompanyApi,
    private userDetailsStore: UserDetailsStore
  ) {}

  private getEndUserInstallations(
    userId: string,
    limit: number,
    cursor: string,
    includeGateways: boolean
  ): Observable<Action> {
    let nextCursor = '';
    const params = cursor ? { limit, includeGateways, cursor } : { limit, includeGateways };

    return this.equipmentApi.getUserInstallationsCollection(userId, params).pipe(
      switchMap((response) => {
        nextCursor = response.cursor.next;

        if (!response.data || !response.data.length) {
          return of([]);
        }

        return of(...response.data).pipe(
          mergeMap((installation) =>
            forkJoin([
              of(installation),
              ...installation.gateways.map((i) => this.getMaterialDetailsForGateway(i)),
            ])
          ),
          map(([installation, ...gateways]) => ({
            ...installation,
            gateways,
          })),
          reduce<Installation, Installation[]>((acc, cur) => [...acc, cur], [])
        );
      }),
      map((installations: Installation[]) =>
        UserDetailsActions.getUserInstallationsSuccess({
          installations,
          cursor: nextCursor,
        })
      ),
      catchError((error) => of(UserDetailsActions.getUserInstallationsError({ error })))
    );
  }

  private getMaterialDetailsForGateway(gateway: Gateway): Observable<Gateway> {
    const mainDevice = gateway.devices.find((dev) => hasMainDeviceRole(dev));

    if (!mainDevice || !mainDevice.boilerSerial) {
      return of(gateway);
    }

    return this.equipmentApi.getMaterialDetails(mainDevice.boilerSerial).pipe(
      map((res) => ({
        ...gateway,
        devices: gateway.devices.map((device) => {
          if (hasMainDeviceRole(device)) {
            return {
              ...device,
              marketingName: res.materialDetails?.[0].description,
            };
          } else {
            return device;
          }
        }),
      })),
      catchError(() => of(gateway))
    );
  }
}
